import React, { memo, useCallback, useContext, useMemo, useState } from 'react'
import { getTenantModules, updateTenantModules } from '../api'
import { DialogPopup, Retrievable } from '../components'
import { FeedbackContext } from '../feedback'
import { Griddable } from '../grid'
import { translate } from '../i18n'
import { Module, TenantModulesResponse } from '../model'
import { TenantModulesFormProps } from './TenantModulesForm.types'
import { Card } from '@mui/material'

function TenantModulesForm({ tenantId, onClose }: TenantModulesFormProps) {
    const feedbackContext = useContext(FeedbackContext)
    const [data, setData] = useState<TenantModulesResponse>()
    const [selected, setSelected] = useState<string[]>([])

    const required = useMemo<string[]>(() => ['BASE'], [])

    const retrievePromise = useCallback(() => {
        return getTenantModules(tenantId)
    }, [tenantId])

    const updateData = useCallback((data: TenantModulesResponse) => {
        setSelected(data.enabled_modules)
        setData(data)
    }, [])

    const onUpdate = () => {
        feedbackContext.showBackdrop()

        updateTenantModules(tenantId, { modules: selected })
            .then((response) => {
                feedbackContext.showSuccess(
                    translate('tenant_modules.success', {
                        tenant: data?.tenant.name,
                        count: selected.length,
                    }) as string
                )

                onClose()
            })
            .catch((error) => feedbackContext.showError(error.message))
            .finally(feedbackContext.closeBackdrop)
    }

    return (
        <DialogPopup
            open
            title={translate('tenant_modules.title')}
            onClose={onClose}
            button={{
                text: translate('buttons.update'),
                onClick: onUpdate,
            }}
        >
            <Retrievable
                getPromise={retrievePromise}
                setData={updateData}
                builder={() => (
                    <Card variant="outlined">
                        <Griddable
                            items={data?.modules || []}
                            noBorderLastRow
                            empty={translate('tenant_modules.empty') as string}
                            selectable={{
                                selected,
                                onChange(ids, items) {
                                    setSelected(ids)
                                },
                                fixed: required,
                                disabled: required,
                            }}
                            getId={(module: Module) => module.id}
                            columns={[
                                {
                                    title: translate('tenant_modules.name'),
                                    converter: (module: Module) => module.name,
                                    id: 'name',
                                },
                                {
                                    title: translate(
                                        'tenant_modules.description'
                                    ),
                                    converter: (module: Module) =>
                                        module.description,
                                    id: 'description',
                                },
                            ]}
                        />
                    </Card>
                )}
            />
        </DialogPopup>
    )
}

export default memo(TenantModulesForm)
